import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  useTheme,
  create,
  color,
  Color,
  MouseCursorStyle,
  percent,
  Label,
  IDisposer,
  Image,
} from "@amcharts/amcharts4/core";
import ReactDOMServer from "react-dom/server";
import am4geodata_region_usa_inLow from "@amcharts/amcharts4-geodata/region/usa/inLow";
import {
  MapChart,
  HeatLegend,
  projections,
  MapImageSeries,
  MapPolygonSeries,
} from "@amcharts/amcharts4/maps";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import {
  POSITIVE_TEST_RATE,
  POSITIVE_TEST_RATE_DARK,
  COLOR_POSITIVE,
  COLOR_TESTED,
  COLOR_DEATHS,
} from "../../../GlobalColors";
import { Row, Col, ButtonGroup, Button, Alert } from "react-bootstrap";
import { FeatureCollection } from "@amcharts/amcharts4-geodata/.internal/Geodata";
import { FullToggle } from "../../../components/FullToggle";
import { roundFloat } from "../../../utils/DataConversion";
import AlertDarkIcon from "../../../assets/alert-dark.svg";
import AlertLightIcon from "../../../assets/alert-light.svg";

useTheme(am4themes_animated);

interface LTCMapProps {
  data: App.CountyStats[];
  currentFilter: App.CurrentFilter;
  setCurrentFilter: (filterSet: App.CurrentFilter) => void;
  resetFilter: () => void;
  metricMapDate: string;
  outliers?: Data.Outlier[];
}

export default function FacilityMap(props: LTCMapProps) {
  const {
    data,
    currentFilter,
    setCurrentFilter,
    resetFilter,
    metricMapDate,
    outliers,
  } = props;
  const [activeTab, setActiveTab] = useState<
    "cases" | "deaths" | "tested" | "positivity" | "metric"
  >("metric");
  const [scaleLog, setScaleLog] = useState(true);
  const [perCapita, setPerCapita] = useState(false);
  const [, setActiveFilterType] = useState("");
  const [testAdmin, setTestAdmin] = useState(true);
  const [metricMap, setMetricMap] = useState<
    "advisory" | "weekly" | "perCapita" | "positivityRate"
  >("weekly");

  const filRef = useRef<App.Filter[]>([]);
  const regionRef = useRef<App.CurrentFilter>();

  const setMap = (perCap: boolean) => {
    setPerCapita(perCap);
    perCapita ? setScaleLog(true) : setScaleLog(false);
  };

  // todo clean-up
  const formatNumber = useCallback(
    (n: number | undefined) => {
      if (!n) return "0";
      if ((perCapita || activeTab === "positivity") && n !== 0) {
        return n.toFixed(1);
      } else {
        n === 0 ? (n = 0) : (n = n = Math.round(n));
      }
      return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    [perCapita, activeTab]
  );

  const getScale = useCallback(
    (n: number) => {
      if (n < 1) {
        return n;
      }
      if (scaleLog) {
        if (Math.log(n) > 0) {
          return Math.log(n);
        } else {
          return Math.log(n * 2) / 2;
        }
      } else {
        return n;
      }
      // return scaleLog ? Math.log(n) : n;
    },
    [scaleLog]
  );

  let minColor: string = "",
    maxColor: string = "";
  if (activeTab === "cases") {
    minColor = COLOR_POSITIVE;
    maxColor = "#eb9700";
  } else if (activeTab === "deaths") {
    minColor = COLOR_DEATHS;
    maxColor = "#ab0e02";
  } else if (activeTab === "tested") {
    minColor = COLOR_TESTED;
    maxColor = "#06428f";
  } else if (activeTab === "positivity") {
    minColor = POSITIVE_TEST_RATE;
    maxColor = POSITIVE_TEST_RATE_DARK;
  }

  const polygonHit = useCallback(
    (env) => {
      // todo - active now works, we need to revamp this abit
      // env.target.isActive = !env.target.isActive;
      let { dataContext } = env.target.dataItem;
      const clickedCounty = {
        name: dataContext.name,
        id: dataContext.id,
        regionId: dataContext.region,
      };

      if (filRef.current.length > 0) {
        let matched = false;
        filRef.current.forEach(function (c, i) {
          if (c.name === clickedCounty.name) {
            matched = true;
            filRef.current = filRef.current.filter(
              (county) => county.name !== clickedCounty.name
            );
            filRef.current = filRef.current.filter(
              (county) => county.name !== clickedCounty.name
            );
          }
        });
        if (!matched) {
          filRef.current.push(clickedCounty);
        }
      } else {
        filRef.current.push(clickedCounty);
      }

      let countyNames = "";

      filRef.current.forEach(function (county, i) {
        countyNames += county.name + " County";
        if (filRef.current.length > 1 && i < filRef.current.length - 1) {
          countyNames += ", ";
        }
      });

      // if a region is active, and all active counties have been deselected, go back to district filter
      if (
        regionRef.current &&
        regionRef.current.filterType === "regional" &&
        filRef.current.length === 0
      ) {
        setCurrentFilter(regionRef.current);
      } else {
        const newFilterSet: App.CurrentFilter = {
          filterType: "county",
          displayName: countyNames,
          filters: filRef.current,
        };

        if (filRef.current.length > 0) {
          // if multiple of the same region are selected, we need to preserve
          // the region designation
          const regions = filRef.current.map((ref) => ref.regionId);
          const uniqueRegionsCount = regions.filter(
            (v, i, a) => a.indexOf(v) === i
          ).length;
          if (uniqueRegionsCount === 1) {
            newFilterSet.regionName = `District ${dataContext.region}`;
            newFilterSet.regionId = dataContext.region;
          }
        }

        setCurrentFilter(newFilterSet);
      }
    },
    [setCurrentFilter]
  );

  const mapContainer = useRef<HTMLDivElement>(null);
  const baseFeatureCollection = useRef<FeatureCollection>();
  const mapRef = useRef<MapChart>();
  const labelRef = useRef<MapImageSeries>();
  const polygons = useRef<MapPolygonSeries>();
  const legendRef = useRef<HeatLegend>();
  const eventDisposer = useRef<IDisposer[]>([]);
  const activeRegionRef = useRef<{ id: string; name: string }>({
    id: "0",
    name: "statewide",
  });

  const buildChart = useCallback(() => {
    if (!mapContainer.current) return;

    const map = create(mapContainer.current, MapChart);

    map.projection = new projections.Miller();
    map.seriesContainer.draggable = false;
    map.seriesContainer.resizable = false;
    map.mouseWheelBehavior = "none";
    map.maxZoomLevel = 1;
    const localGeo = am4geodata_region_usa_inLow;
    // need to add region
    localGeo.features = am4geodata_region_usa_inLow.features.map((feature) => ({
      ...feature,
      properties: {
        ...feature.properties,
        regionId: data.find((record) => record.id === feature.id)?.region,
      },
    }));
    //map.geodata = localGeo;
    baseFeatureCollection.current = localGeo;

    // Create map polygon series
    var polygonSeries = map.series.push(new MapPolygonSeries());

    // Make map load polygon (like country names) data from GeoJSON
    polygonSeries.useGeodata = true;

    // Configure series
    let polygonTemplate = polygonSeries.mapPolygons.template;
    polygonTemplate.nonScalingStroke = true;
    polygonTemplate.strokeWidth = 0.5;
    polygonTemplate.stroke = color("#000");
    polygonTemplate.tooltipHTML = `{tooltip}`;
    polygonTemplate.cursorOverStyle = MouseCursorStyle.pointer;

    polygons.current = polygonSeries;

    var labelSeries = map.series.push(new MapImageSeries());
    var labelTemplate = labelSeries.mapImages.template.createChild(Label);
    labelTemplate.horizontalCenter = "middle";
    labelTemplate.verticalCenter = "middle";
    labelTemplate.fontSize = 11;

    labelTemplate.interactionsEnabled = false;

    labelRef.current = labelSeries;

    let legend = create("legendDiv", HeatLegend);
    legendRef.current = legend;

    mapRef.current = map;

    return () => map.dispose();
  }, [data]);

  const injectPolygons = useCallback(() => {
    if (
      !polygons.current ||
      !legendRef.current ||
      !mapRef.current ||
      !baseFeatureCollection.current
    )
      return;
    const existingLegend = legendRef.current;
    existingLegend.dispose();
    const polygonSeries = polygons.current;

    // remove existing heat rules
    if (polygonSeries.heatRules.length) {
      polygonSeries.heatRules.clear();
    }

    polygonSeries.heatRules.push({
      property: "fill",
      target: polygons.current.mapPolygons.template,
      min: color(minColor).lighten(1),
      max: color(maxColor).brighten(-0.05),
    });

    function getColor(score: number) {
      if (score <= 0.5) {
        return color("#35587f");
      } else if (score <= 1.5) {
        return color("#f9d978");
      } else if (score <= 2.5) {
        return color("#e99e50");
      } else if (score >= 3) {
        return color("#94432b");
      }
    }

    function fontColor(score: number) {
      if (score <= 0.5) {
        return color("white");
      } else if (score <= 1.5) {
        return color("black");
      } else if (score <= 2.5) {
        return color("black");
      } else if (score >= 3) {
        return color("white");
      }
    }

    function generateBaseTooltip(
      countyName: string,
      caseCount: number,
      newCaseCount: number,
      deathCount: number,
      newDeathCount: number,
      testCount: number,
      newTestCount: number,
      positivityRate: number | undefined,
      positivityRateAll: number | undefined
    ) {
      return ReactDOMServer.renderToString(
        <div>
          <p className="font-weight-bold text-center">{countyName}</p>
          <p className="mb-0">
            <span className="font-weight-bold">
              {perCapita ? "Cases / Population" : "Positive Cases"}
            </span>
            : {caseCount} ({newCaseCount} new)
          </p>
          <p className="mb-0">
            <span className="font-weight-bold">
              {perCapita ? "Deaths / Population" : "Deaths"}
            </span>
            : {deathCount} ({newDeathCount} new)
          </p>
          <p className="mb-0">
            <span className="font-weight-bold">
              {perCapita
                ? "Tested Individuals / Population"
                : "Tested Individuals"}
            </span>
            : {testCount} ({newTestCount} new)
          </p>
          <p className="mb-0">
            <span className="font-weight-bold">
              7-Day Positivity - All Tests
            </span>
            : {roundFloat(positivityRateAll, 1)} %
          </p>
          <p className="mb-0">
            <span className="font-weight-bold">
              7-Day Positivity - Unique Individuals
            </span>
            : {roundFloat(positivityRate, 1)} %
          </p>
        </div>
      );
    }
    function generateMetricTooltip(
      countyName: string,
      perCapita: number,
      perCapitaScore: number,
      positivityRate: number,
      positivityRateScore: number,
      positivityRateDelta: number,
      weeklyScore: number,
      advisory: string,
      alerts: string[] = []
    ) {
      return ReactDOMServer.renderToString(
        <div className="tooltip-container">
          <p className="font-weight-bold text-center">{countyName}</p>
          <p className="mb-0">
            <span className="font-weight-bold">Weekly 2-Metric Score</span>:{" "}
            {weeklyScore}
          </p>
          <p className="mb-0">
            <span className="font-weight-bold">Advisory Level</span>: {advisory}
          </p>
          <p className="mb-0">
            <span className="font-weight-bold">
              Weekly Cases Per 100,000 Residents ({perCapitaScore})
            </span>
            : {perCapita}
          </p>
          <p className="mb-0">
            <span className="font-weight-bold">
              7-Day All Tests Positivity Rate ({positivityRateScore})
            </span>
            : {positivityRate} %{" "}
          </p>
          <p className="mb-0">
            <span className="font-weight-bold">Positivity Change:</span>{" "}
            {positivityRateDelta > 0 ? (
              <i className="fas fa-arrow-up"></i>
            ) : positivityRateDelta === 0 ? (
              <span>No Change</span>
            ) : (
              <i className="fas fa-arrow-down"></i>
            )}
          </p>
          {alerts.length ? (
            <React.Fragment>
              <hr />
              <div>
                <img src={AlertDarkIcon} width={20} alt="Alert Icon" />{" "}
                <span className="font-weight-bold">Alerts</span>
                {alerts.map((alert, index) => (
                  <p key={index} className="alert-text text-left">
                    {alert}
                  </p>
                ))}
              </div>
            </React.Fragment>
          ) : null}
        </div>
      );
    }

    const selectedRegion = activeRegionRef.current;

    if (activeRegionRef.current.id === "0") {
      mapRef.current.geodata = { ...baseFeatureCollection.current };
    } else {
      const geo = { ...baseFeatureCollection.current };
      geo.features = geo.features.filter(
        (record: any) => record.properties.regionId === selectedRegion.id
      );
      mapRef.current.geodata = geo;
    }

    const mapData = mapRef.current.geodata as FeatureCollection;

    function roundRate(rate: number) {
      if (!rate) return 0;
      return Math.round(rate * 10) / 10;
    }

    const mapValues = mapData.features.map((county: any) => {
      const dataItem = data.find(
        (feature) => feature.id === county.properties.id
      ) as App.CountyStats;
      const population = dataItem.population;
      const region = dataItem.region;
      let cases = dataItem.cases;
      let deaths = dataItem.deaths;
      let tested = dataItem.tests;
      let hasAlert = false;
      const positivity = testAdmin
        ? roundRate(dataItem.positivityRateAdmin ?? 0)
        : roundRate(dataItem.positivityRate ?? 0);

      let countyScore: number;

      switch (metricMap) {
        case "advisory":
          countyScore = dataItem.advisoryScore;
          break;
        case "weekly":
          countyScore = dataItem.weeklyScore;
          break;
        case "perCapita":
          countyScore = dataItem.weeklyPerCapitaScore;
          break;
        case "positivityRate":
          countyScore = dataItem.weeklyPositivityScore;
          break;
        default:
          countyScore = 0;
      }

      function generatePerCap(events: number) {
        if (!population) return 1;
        return parseFloat(((events / population) * 100000).toFixed(1));
      }

      if (perCapita) {
        // todo insert population
        cases = generatePerCap(dataItem.cases);
        deaths = generatePerCap(dataItem.deaths);
        tested = generatePerCap(dataItem.tests);
      }

      let alerts: string[] = [];

      if (outliers && outliers.length) {
        const countyOutlier = outliers.find(
          (outlier) => outlier.county_name === county.properties.name
        );
        alerts = countyOutlier?.alerts ?? [];
      }

      let value = 0;
      let label: number | string;
      let textColor: Color | undefined;
      let fill: Color | undefined;

      function acroynm(value: number, short: boolean = true) {
        switch (value) {
          case 0:
            return short ? "BL" : "Blue";
          case 1:
            return short ? "YL" : "Yellow";
          case 2:
            return short ? "OR" : "Orange";
          case 3:
            return short ? "RD" : "Red";
          default:
            return short ? "E" : "Error";
        }
      }

      switch (activeTab) {
        case "cases":
          value = getScale(cases);
          label = cases;
          break;
        case "deaths":
          value = getScale(deaths);
          label = deaths;
          break;
        case "tested":
          value = getScale(tested);
          label = tested;
          break;
        case "positivity":
          value = getScale(positivity ?? 0);
          label = positivity ?? 0;
          break;
        case "metric":
          if (alerts.length) hasAlert = true;
          value = countyScore;
          label = countyScore;
          if (metricMap === "advisory") label = acroynm(countyScore);
          textColor = fontColor(countyScore ?? 0);
          fill = getColor(countyScore ?? 0);
          break;
      }

      return {
        ...county,
        value: value,
        label: label,
        alert: hasAlert,
        color: textColor,
        region: region,
        fill: fill,
        tooltip:
          activeTab !== "metric"
            ? generateBaseTooltip(
                county.properties.name,
                cases,
                dataItem.newCases,
                deaths,
                dataItem.newDeaths,
                tested,
                dataItem.newTests,
                dataItem.positivityRate,
                dataItem.positivityRateAdmin
              )
            : generateMetricTooltip(
                county.properties.name,
                dataItem.weeklyPerCapita,
                dataItem.weeklyPerCapitaScore,
                dataItem.weeklyPositivityRate,
                dataItem.weeklyPositivityScore,
                dataItem.weeklyPositivityRateDelta,
                dataItem.weeklyScore,
                acroynm(dataItem.advisoryScore, false),
                alerts
              ),
      };
    });

    polygonSeries.data = mapValues;

    if (activeTab !== "metric") {
      let legend = create("legendDiv", HeatLegend);

      const minValue = Math.min.apply(
        null,
        mapValues.map((county) => county.value)
      );
      const minLabel = Math.min.apply(
        null,
        mapValues.map((county) => county.label)
      );
      const maxValue = Math.max.apply(
        null,
        mapValues.map((county) => county.value)
      );
      const maxLabel = Math.max.apply(
        null,
        mapValues.map((county) => county.label)
      );

      legend.series = polygonSeries;
      legend.align = "center";
      legend.valign = "bottom";
      legend.width = percent(90);
      legend.fontSize = 11;
      legend.minColor = color(minColor).lighten(1);
      legend.maxColor = color(maxColor).brighten(-0.05);

      let minRange = legend.valueAxis.axisRanges.create();
      minRange.value = minValue;
      minRange.label.text = formatNumber(minLabel);
      let maxRange = legend.valueAxis.axisRanges.create();
      maxRange.value = maxValue;
      maxRange.label.text = formatNumber(maxLabel);

      if (scaleLog) {
        // Set up internal legend values for logarithmic scale
        let lastMaxLabel = maxLabel;
        // if (maxLabel > 10) {
        for (let i = 2; i < 6; i++) {
          let midRange = legend.valueAxis.axisRanges.create();
          if (midRange.value !== 0) {
            midRange.value = getScale((lastMaxLabel - minLabel) / i);
            midRange.label.text = formatNumber((lastMaxLabel - minLabel) / i);
          }
          lastMaxLabel = (lastMaxLabel - minLabel) / i;
          if (lastMaxLabel < minLabel + 0.5) {
            midRange.label.text = "";
          }
        }
        // }
        // Blank out internal heat legend value axis labels
        legend.valueAxis.renderer.labels.template.adapter.add("text", function (
          labelText
        ) {
          return "";
        });
      }
      legendRef.current = legend;
    } else {
      const template = polygonSeries.mapPolygons.template;
      template.propertyFields.fill = "fill";
    }
  }, [
    activeTab,
    formatNumber,
    getScale,
    maxColor,
    minColor,
    scaleLog,
    perCapita,
    data,
    testAdmin,
    metricMap,
    outliers,
  ]);

  const updateEvents = useCallback(() => {
    if (!polygons.current) return;
    const polygonSeries = polygons.current;
    const labelSeries = labelRef.current;

    // clear all events to prevent build up
    eventDisposer.current.forEach((event) =>
      !event.isDisposed() ? event.dispose() : null
    );

    if (labelSeries) {
      labelSeries.disposeChildren();
      const validateEvent = polygonSeries.events.on("validated", function () {
        polygonSeries.mapPolygons.each(function (polygon) {
          let label = labelSeries.mapImages.create();
          const dataContext = polygon.dataItem.dataContext as any;
          let state = dataContext;
          label.latitude = polygon.visualLatitude;
          label.longitude = polygon.visualLongitude;
          const firstLabel = label.children.getIndex(0) as any;
          if (!firstLabel) return;
          firstLabel.text = state.label;
          firstLabel.fill = state.color ?? color("black");
          if (!state.alert || metricMap === "advisory") return;
          let alertLabel = firstLabel.createChild(Image) as Image;
          // todo make this easier
          alertLabel.href =
            state.color.hex === "#ffffff" ? AlertLightIcon : AlertDarkIcon;
          alertLabel.width = 10;
          alertLabel.dy = -20;
          alertLabel.dx = state.label.toString().length > 1 ? 20 : 10;
        });
      });

      eventDisposer.current.push(validateEvent);
    }

    const polygonTemplate = polygonSeries.mapPolygons.template;

    const clickEvent = polygonTemplate.events.on("hit", polygonHit);

    eventDisposer.current.push(clickEvent);
  }, [polygonHit, metricMap]);

  useEffect(() => {
    buildChart();
    // only desire to build chart once
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!polygons.current) return;
    // hack part 2
    injectPolygons();
    updateEvents();
    // eslint-disable-next-line
  }, [updateEvents, perCapita, metricMap, currentFilter, testAdmin, activeTab]);

  const onRegionSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setActiveFilterType("regional");
    resetFilter();
    if (e.currentTarget.value === "statewide") {
      setCurrentFilter({
        filterType: "state",
        displayName: "Statewide",
        filters: [],
      });
      activeRegionRef.current = {
        id: "0",
        name: "statewide",
      };
      filRef.current = [];
      regionRef.current = undefined;
    } else {
      let region: App.CurrentFilter = {
        filterType: "regional",
        displayName: "District " + e.currentTarget.value,
        filters: data
          .filter((record) => record.region === e.currentTarget.value)
          .map((record) => ({
            id: record.id,
            name: record.name ?? "",
            regionId: record.region ?? "",
          })),
      };
      setCurrentFilter(region);
      activeRegionRef.current = {
        id: e.currentTarget.value,
        name: "District " + e.currentTarget.value,
      };
      regionRef.current = region;
    }
  };

  const onMetricMapSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setMetricMap(
      e.target.value as "advisory" | "weekly" | "perCapita" | "positivityRate"
    );
  };

  const onFilterReset = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    filRef.current = [];
    if (
      activeRegionRef.current.id !== "statewide" &&
      currentFilter.filterType === "county" &&
      regionRef.current
    ) {
      setCurrentFilter(regionRef.current);
    } else {
      activeRegionRef.current = {
        id: "0",
        name: "statewide",
      };
      setCurrentFilter({
        filterType: "state",
        displayName: "Statewide",
        filters: [],
      });
      resetFilter();
    }
  };

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <h3 className="mb-1">County Distributions</h3>
          <p className="mb-2">Select a county below by tap or click </p>
        </Col>
      </Row>
      <div className="map-btns-wrapper ml-0 mr-0 mb-3">
        <ButtonGroup className="text-center map-btns">
          <Button
            className={`${
              activeTab === "cases" ? "active" : ""
            } btn-maps-toggle`}
            onClick={() => setActiveTab("cases")}
          >
            Cases
          </Button>
          <Button
            className={`${
              activeTab === "deaths" ? "active" : ""
            } btn-maps-toggle`}
            onClick={() => setActiveTab("deaths")}
          >
            Deaths
          </Button>
          <Button
            className={`${
              activeTab === "tested" ? "active" : ""
            } btn-maps-toggle`}
            onClick={() => setActiveTab("tested")}
          >
            Tested
          </Button>
          <Button
            className={`${
              activeTab === "positivity" ? "active" : ""
            } btn-maps-toggle`}
            onClick={() => setActiveTab("positivity")}
          >
            Positivity
          </Button>
          <Button
            className={`${
              activeTab === "metric" ? "active" : ""
            } btn-maps-toggle`}
            onClick={() => setActiveTab("metric")}
          >
            County Metrics
          </Button>
        </ButtonGroup>
      </div>
      <Row className="m-map-toolbar no-gutters">
        {activeTab === "positivity" ? (
          <Col xs={12} sm={6} className="d-flex mb-3 justify-content-center">
            <FullToggle
              options={["All Tests", "Individuals"]}
              checked={!testAdmin}
              setChecked={() => setTestAdmin(!testAdmin)}
            />
          </Col>
        ) : activeTab === "metric" ? (
          <React.Fragment>
            <Col className="mb-4" xs={12} sm={12}>
              Below results are as of{" "}
              <span className="font-weight-bold">{metricMapDate}</span>. The
              county metrics map is updated Wednesdays at{" "}
              <span className="font-weight-bold">12:00 PM</span> and reflects
              data through the previous Sunday.
              <div>
                <a
                  href="./CountyScoringMapDetails.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Click here to learn more about the county metrics map
                </a>
              </div>
            </Col>
            <Col xs={12} sm={6} className="justify-content-center">
              <div className="m-map-regions d-flex align-items-center justify-content-center mb-3">
                <p className="mb-0 pr-2">Map Filter</p>
                <div className="select-wrapper lg">
                  <select
                    className="m-map-metric-select"
                    value={metricMap}
                    onChange={onMetricMapSelect}
                    disabled={false}
                  >
                    <option value="weekly">Weekly 2-Metric Score</option>
                    <option value="advisory">Advisory Level</option>
                    <option value="perCapita">
                      Weekly Cases Per 100,000 Residents
                    </option>
                    <option value="positivityRate">
                      7-Day All Tests Positivity Rate
                    </option>
                  </select>
                </div>
              </div>
            </Col>
          </React.Fragment>
        ) : (
          <Col xs={12} sm={6} className="mb-3 d-flex justify-content-center">
            <FullToggle
              options={["Total Counts", "Per 100,000 residents"]}
              checked={perCapita}
              setChecked={setMap}
            />
          </Col>
        )}
        <Col
          xs={12}
          sm={{
            offset:
              activeTab === "positivity" || activeTab === "metric"
                ? undefined
                : "6",
            span: "6",
          }}
          className="justify-content-sm-center"
        >
          <div className="m-map-regions d-flex align-items-center justify-content-center mb-3">
            <p className="mb-0 pr-2">IDOH District</p>
            <div className="select-wrapper">
              <select
                className="m-map-region-select"
                value={activeRegionRef.current.id}
                onChange={onRegionSelect}
                disabled={false}
              >
                <option value="statewide">Statewide</option>
                <option value="1">District 1</option>
                <option value="2">District 2</option>
                <option value="3">District 3</option>
                <option value="4">District 4</option>
                <option value="5">District 5</option>
                <option value="6">District 6</option>
                <option value="7">District 7</option>
                <option value="8">District 8</option>
                <option value="9">District 9</option>
                <option value="10">District 10</option>
              </select>
            </div>
          </div>
        </Col>
      </Row>
      <Row className="mt-2 text-left">
        {activeTab === "metric" ? (
          <Col xs={12} md={4}>
            <Col>
              <h5>Advisory Level</h5>
              <p className="mt-0 text-seconday">
                Advisory level refers to the current guidelines the county must
                follow. A county must remain at a lower Weekly Two-Metric Score
                for two consecutive weeks to move down to a lower advisory
                level.
              </p>
              <h5>Weekly 2-Metric Score</h5>
              <ul className="list-unstyled">
                <li>
                  <span style={{ color: "#35587f" }}>{"\u25A0"} </span>Blue (0
                  and .5)
                </li>
                <li>
                  <span style={{ color: "#f9d978" }}>{"\u25A0"} </span>Yellow (1
                  and 1.5)
                </li>
                <li>
                  <span style={{ color: "#e99e50" }}>{"\u25A0"} </span>Orange (2
                  and 2.5)
                </li>
                <li>
                  <span style={{ color: "#94432b" }}>{"\u25A0"} </span>Red (3)
                </li>
              </ul>
            </Col>
            <Col>
              <h5>Weekly Cases Per 100,000 Residents</h5>
              <ul className="list-unstyled">
                <li>
                  <span style={{ color: "#35587f" }}>{"\u25A0"} </span>Less Than
                  10 new cases(0)
                </li>
                <li>
                  <span style={{ color: "#f9d978" }}>{"\u25A0"} </span>10 to 99
                  new cases(1)
                </li>
                <li>
                  <span style={{ color: "#e99e50" }}>{"\u25A0"} </span>100 to
                  199 new cases(2)
                </li>
                <li>
                  <span style={{ color: "#94432b" }}>{"\u25A0"} </span>200 or
                  more(3)
                </li>
              </ul>
            </Col>
            <Col>
              <h5>7-Day All Tests Positivity Rate</h5>
              <ul className="list-unstyled">
                <li>
                  <span style={{ color: "#35587f" }}>{"\u25A0"} </span>Less than
                  5% (0)
                </li>
                <li>
                  <span style={{ color: "#f9d978" }}>{"\u25A0"} </span>5% to
                  9.9% (1)
                </li>
                <li>
                  <span style={{ color: "#e99e50" }}>{"\u25A0"} </span>10% to
                  14.9% (2)
                </li>
                <li>
                  <span style={{ color: "#94432b" }}>{"\u25A0"} </span>15% or
                  greater (3)
                </li>
              </ul>
            </Col>
            <Col>
              <p>
                The <img src={AlertDarkIcon} width={16} alt="Alert Icon" />{" "}
                indicates a disclaimer alert for the county. Hover over the
                symbol for additional details.{" "}
              </p>
            </Col>
          </Col>
        ) : null}
        <Col xs={12} md={{ offset: activeTab === "metric" ? 0 : 2, span: 8 }}>
          <div
            ref={mapContainer}
            style={{
              width: "100%",
              height: "600px",
              margin: "20px 0 40px",
            }}
          ></div>
        </Col>
      </Row>
      <div id="legendDiv"></div>
      <Alert
        show={currentFilter.filters.length > 0}
        className="pulse mt-3 mb-0"
        style={{
          backgroundColor: "#2a4d87",
          color: "white",
        }}
        key="1"
        variant="info"
      >
        <Row>
          <Col sm={12} md={8}>
            <span className="font-weight-bold">Filtering on: </span>
            {currentFilter.displayName}
          </Col>
          <Col sm={12} md={4}>
            <Button
              className="w-100"
              onClick={onFilterReset}
              variant="outline-light"
            >
              Reset
            </Button>
          </Col>
        </Row>
      </Alert>
    </React.Fragment>
  );
}
